import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  cancelOrders,
  listOrders,
  markPaidBulk,
  type AdminOrderRow,
} from '../api/orders';
import { StatusPill } from '../components/StatusPill';
import type { OrderStatus } from '../types';
import { ManualAddModal } from './ManualAddModal';
import { MergeModal, ModalFoot, ModalHead, ModalShell } from './MergeModal';
import { formatGroszeAsZl } from '../util/money';
import { isDemoMode } from './demoMode';

// Plain <a href> downloads bypass api/client.ts, so demo mode has to neutralise them here or a
// presentation would hand out a CSV of real customers.
function ExportLink({ href, hint, children }: { href: string; hint?: string; children: React.ReactNode }) {
  const demo = isDemoMode();
  return (
    <a
      href={demo ? undefined : href}
      aria-disabled={demo || undefined}
      title={demo ? 'Tryb demo: eksport wyłączony' : hint}
      onClick={demo ? (e) => e.preventDefault() : undefined}
      className="btn-a"
      style={{ fontSize: 12, opacity: demo ? 0.45 : 1, cursor: demo ? 'not-allowed' : undefined }}
    >
      {children}
    </a>
  );
}

const FILTERS: { label: string; status?: OrderStatus }[] = [
  { label: 'Wszystkie' },
  { label: 'Czekają na płatność', status: 'awaiting_payment' },
  { label: 'Opłacone', status: 'paid' },
  { label: 'Wysłane', status: 'shipped' },
  { label: 'Odbiór', status: 'pickup' },
  { label: 'Anulowane', status: 'cancelled' },
];

const PAGE_SIZES = [50, 100, 200] as const;

type Props = { modal?: 'manual' };

// Every sweep here acts on a selection that only exists in this component's state, so the modals
// are opened in place. Routing to them instead drops the selection on the remount.
type BulkAction = 'merge' | 'markPaid' | 'cancel';

export function OrdersList({ modal }: Props) {
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q')?.trim() || undefined;
  const [orders, setOrders] = useState<AdminOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [active, setActive] = useState<string>('Wszystkie');
  // Keyed by id and holding the row itself, so the selection survives page turns, filters and
  // searches: merging two parcels often means picking orders that never share a page.
  const [sel, setSel] = useState<Map<string, AdminOrderRow>>(new Map());
  const [bulk, setBulk] = useState<BulkAction | null>(null);

  useEffect(() => {
    setOffset(0);
  }, [q]);

  const reload = useCallback(async () => {
    const status = FILTERS.find((f) => f.label === active)?.status;
    try {
      const r = await listOrders({
        limit: String(pageSize),
        offset: String(offset),
        status,
        q,
      });
      setOrders(r.orders);
      setTotal(r.total);
      setSel((prev) => {
        if (prev.size === 0) return prev;
        const next = new Map(prev);
        for (const o of r.orders) if (next.has(o.id)) next.set(o.id, o);
        return next;
      });
    } catch {
      /* keep the last good page on screen */
    }
  }, [active, q, offset, pageSize]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const afterSweep = async () => {
    setSel(new Map());
    setBulk(null);
    await reload();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.floor(offset / pageSize) + 1;
  const firstRow = total === 0 ? 0 : offset + 1;
  const lastRow = Math.min(offset + pageSize, total);
  const canPrev = offset > 0;
  const canNext = offset + pageSize < total;

  const toggleSel = (o: AdminOrderRow) => {
    setSel((prev) => {
      const next = new Map(prev);
      if (next.has(o.id)) next.delete(o.id);
      else next.set(o.id, o);
      return next;
    });
  };

  const unselect = (id: string) => {
    setSel((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const allOnPageSelected = orders.length > 0 && orders.every((o) => sel.has(o.id));
  const toggleSelAll = () => {
    setSel((prev) => {
      const next = new Map(prev);
      for (const o of orders) {
        if (allOnPageSelected) next.delete(o.id);
        else next.set(o.id, o);
      }
      return next;
    });
  };

  const activeStatus = FILTERS.find((f) => f.label === active)?.status;
  const listFilterParams = new URLSearchParams();
  if (activeStatus) listFilterParams.set('status', activeStatus);
  if (q) listFilterParams.set('q', q);
  const listFilterQuery = listFilterParams.toString() ? `?${listFilterParams}` : '';
  const printListHref = `/api/admin/print-list.csv${listFilterQuery}`;
  const ordersListHref = `/api/admin/orders-list.csv${listFilterQuery}`;

  const selectedRows = [...sel.values()];
  const selectedIds = [...sel.keys()];
  const selOffPage = selectedIds.filter((id) => !orders.some((o) => o.id === id)).length;
  const selSumGrosze = selectedRows.reduce((s, o) => s + o.totalGrosze, 0);

  return (
    <>
      <div
        className="card"
        style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}
      >
        <span
          className="hide-phone"
          style={{
            fontSize: 11,
            fontFamily: 'var(--display)',
            textTransform: 'uppercase',
            letterSpacing: '.1em',
            color: 'var(--admin-muted)',
          }}
        >
          Filtry:
        </span>
        <ExportLink
          href={printListHref}
          hint="Koszulki z zamówień widocznych na liście (filtr i wyszukiwanie), krój / kolor / rozmiar."
        >
          ↧ Eksport listy do druku
        </ExportLink>
        <ExportLink
          href={ordersListHref}
          hint="Zamówienia widoczne na liście (filtr i wyszukiwanie, bez stronicowania) z kwotą za koszulki i za dostawę."
        >
          ↧ Eksport zamówień
        </ExportLink>
        {active === 'Odbiór' && (
          <ExportLink href="/api/admin/pickup-list.csv">↧ Eksport listy odbiorów</ExportLink>
        )}
        <div className="orders-filters">
          {FILTERS.map((f) => {
            const on = active === f.label;
            return (
              <button
                key={f.label}
                onClick={() => {
                  setActive(f.label);
                  setOffset(0);
                }}
                className="btn-a"
                style={{
                  padding: '5px 10px',
                  background: on ? 'var(--ink)' : 'var(--admin-card)',
                  color: on ? 'var(--paper)' : 'var(--admin-ink)',
                  borderColor: on ? 'var(--ink)' : 'var(--admin-line-strong)',
                  fontSize: 12,
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--admin-muted)' }} className="mono">
          {total}
        </span>
      </div>

      {sel.size > 0 && (
        <div
          style={{
            background: 'var(--ink)',
            color: 'var(--paper)',
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            borderRadius: 6,
            marginBottom: 10,
          }}
        >
          <span className="display" style={{ fontSize: 16, color: 'var(--orange)' }}>
            {sel.size} ZAZNACZONE
          </span>
          {selOffPage > 0 && (
            <span className="mono" style={{ fontSize: 11, color: 'rgba(244,236,221,.7)' }}>
              w tym {selOffPage} poza widokiem
            </span>
          )}
          <span className="hide-phone" style={{ width: 1, height: 18, background: 'rgba(255,255,255,.15)' }} />
          <button
            type="button"
            onClick={() => setBulk('merge')}
            disabled={sel.size < 2}
            title={sel.size < 2 ? 'Zaznacz co najmniej dwa zamówienia' : undefined}
            className="btn-a"
            style={{
              background: 'transparent',
              color: 'var(--paper)',
              borderColor: 'rgba(255,255,255,.2)',
              opacity: sel.size < 2 ? 0.45 : 1,
              cursor: sel.size < 2 ? 'not-allowed' : 'pointer',
            }}
          >
            ⊕ Połącz w paczkę
          </button>
          <button
            type="button"
            onClick={() => setBulk('markPaid')}
            className="btn-a"
            style={{ background: 'transparent', color: 'var(--paper)', borderColor: 'rgba(255,255,255,.2)' }}
          >
            ✓ Oznacz opłacone
          </button>
          <button
            type="button"
            onClick={() => setBulk('cancel')}
            className="btn-a"
            style={{ background: 'transparent', color: '#FF8A6B', borderColor: 'rgba(255,138,107,.3)' }}
          >
            ✕ Anuluj…
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(244,236,221,.5)' }} className="mono">
            SUMA: {formatGroszeAsZl(selSumGrosze).toUpperCase()}
          </span>
          <button
            type="button"
            onClick={() => setSel(new Map())}
            className="btn-a"
            style={{ background: 'transparent', color: 'rgba(244,236,221,.7)', borderColor: 'transparent', fontSize: 12 }}
          >
            Wyczyść zaznaczenie
          </button>
        </div>
      )}

      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="a-table-scroll">
          <table className="orders-table">
            <thead>
              <tr>
                <th className="c-sel">
                  <label className="sel-all-label">
                    <input
                      type="checkbox"
                      aria-label="Zaznacz wszystkie na stronie"
                      checked={allOnPageSelected}
                      onChange={toggleSelAll}
                    />
                    <span>Zaznacz wszystkie</span>
                  </label>
                </th>
                <th>Numer</th>
                <th>Klient</th>
                <th>Zam. zewnętrzne</th>
                <th>Pozycje</th>
                <th>Wysyłka</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Wartość</th>
                <th>Czas</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const checked = sel.has(o.id);
                return (
                  <tr key={o.id} style={{ background: checked ? '#FFF6EC' : undefined }}>
                    <td className="c-sel">
                      <input type="checkbox" checked={checked} onChange={() => toggleSel(o)} />
                    </td>
                    <td className="mono c-num" style={{ fontWeight: 700 }}>
                      <Link to={`/admin/zamowienia/${o.id}`} style={{ color: 'inherit' }}>
                        {o.id}
                      </Link>
                    </td>
                    <td className="c-cust">
                      {o.customerName}
                      {o.undeliveredEmail && (
                        <span
                          role="img"
                          aria-label="E-mail nie dociera"
                          title={`E-mail nie dociera: ${o.undeliveredEmail.recipient}`}
                          style={{ marginLeft: 6, color: 'var(--admin-danger)', fontWeight: 700 }}
                        >
                          ✉!
                        </span>
                      )}
                    </td>
                    <td className="mono c-ext" style={{ color: 'var(--admin-muted)', fontSize: 12 }}>
                      {o.externalOrderId ?? '—'}
                    </td>
                    <td className="c-items">
                      {o.items
                        .map((i) => `${i.cut} · ${i.size}${i.qty > 1 ? ` × ${i.qty}` : ''}`)
                        .join(' + ')}
                      {o.isManual && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 10,
                            padding: '1px 5px',
                            background: '#E0E7F2',
                            color: '#3A4A8C',
                            borderRadius: 3,
                            fontWeight: 700,
                          }}
                        >
                          RĘCZNIE
                        </span>
                      )}
                      {o.mergedIntoId && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 10,
                            padding: '1px 5px',
                            background: '#FAE8C2',
                            color: '#8C6B00',
                            borderRadius: 3,
                            fontWeight: 700,
                          }}
                        >
                          ↔ {o.mergedIntoId.replace('OPT-26-', '+')}
                        </span>
                      )}
                    </td>
                    <td className="mono c-ship" style={{ fontSize: 12 }}>
                      {shippingShort(o)}
                    </td>
                    <td className="c-status">
                      <StatusPill status={(o.effectiveStatus ?? o.status) as OrderStatus} />
                    </td>
                    <td className="mono c-value" style={{ textAlign: 'right', fontWeight: 700 }}>
                      {formatGroszeAsZl(o.totalGrosze)}
                    </td>
                    <td className="mono c-time" style={{ fontSize: 12, color: 'var(--admin-muted)' }}>
                      {timeShort(o.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          style={{
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--admin-line)',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span className="mono" style={{ fontSize: 12, color: 'var(--admin-muted)' }}>
            {total === 0 ? 'Brak wyników' : `${firstRow}–${lastRow} z ${total}`}
            <label style={{ marginLeft: 12 }}>
              Na stronie:{' '}
              <select
                className="input-a"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setOffset(0);
                }}
                style={{ padding: '2px 4px', fontSize: 12 }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              className="btn-a"
              onClick={() => setOffset(Math.max(0, offset - pageSize))}
              disabled={!canPrev}
              style={{
                padding: '5px 10px',
                fontSize: 12,
                opacity: canPrev ? 1 : 0.4,
                cursor: canPrev ? 'pointer' : 'not-allowed',
              }}
            >
              ← Poprzednia
            </button>
            <span className="mono" style={{ fontSize: 12, color: 'var(--admin-muted)', padding: '0 6px' }}>
              Strona {currentPage} z {totalPages}
            </span>
            <button
              type="button"
              className="btn-a"
              onClick={() => setOffset(offset + pageSize)}
              disabled={!canNext}
              style={{
                padding: '5px 10px',
                fontSize: 12,
                opacity: canNext ? 1 : 0.4,
                cursor: canNext ? 'pointer' : 'not-allowed',
              }}
            >
              Następna →
            </button>
          </div>
        </div>
      </div>

      {modal === 'manual' && <ManualAddModal onCreated={reload} />}
      {bulk === 'merge' && (
        <MergeModal
          selected={selectedRows}
          onRemove={unselect}
          onClose={() => setBulk(null)}
          onMerged={afterSweep}
        />
      )}
      {bulk === 'markPaid' && (
        <MarkPaidBulkModal
          ids={selectedIds}
          onClose={() => setBulk(null)}
          onDone={afterSweep}
        />
      )}
      {bulk === 'cancel' && (
        <CancelBulkModal ids={selectedIds} onClose={() => setBulk(null)} onDone={afterSweep} />
      )}
    </>
  );
}

function MarkPaidBulkModal({
  ids,
  onClose,
  onDone,
}: {
  ids: string[];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await markPaidBulk(ids, note.trim() || undefined);
      await onDone();
    } catch (err) {
      setError(bulkErrorMessage(err, 'opłacone'));
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={520}>
      <ModalHead
        title={`Oznacz ${ids.length} zam. jako opłacone`}
        subtitle="OPERACJA ZBIORCZA"
        onClose={onClose}
      />
      <div style={{ padding: 22 }}>
        <p style={{ margin: 0, fontSize: 13 }}>
          Każde z zaznaczonych zamówień dostanie status <b>opłacone</b>, płatność ręczną i maila z
          potwierdzeniem. Operacji nie cofniesz.
        </p>
        <div className="mono" style={{ marginTop: 10, fontSize: 12, color: 'var(--admin-muted)' }}>
          {ids.join(' · ')}
        </div>
        {error && <BulkError message={error} />}
        <div className="field" style={{ marginTop: 14 }}>
          <label htmlFor="bulk-paid-note" style={{ fontSize: 11 }}>
            Notatka do dziennika (opcjonalna)
          </label>
          <input
            id="bulk-paid-note"
            className="input-a"
            style={{ width: '100%', marginTop: 4 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
      <ModalFoot>
        <button className="btn-a" onClick={onClose} disabled={submitting}>
          Zamknij
        </button>
        <button className="btn-a primary" onClick={submit} disabled={submitting}>
          Potwierdź i oznacz opłacone
        </button>
      </ModalFoot>
    </ModalShell>
  );
}

function CancelBulkModal({
  ids,
  onClose,
  onDone,
}: {
  ids: string[];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [refundNote, setRefundNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await cancelOrders(ids, reason.trim(), refundNote.trim());
      await onDone();
    } catch (err) {
      setError(bulkErrorMessage(err, 'anulowane'));
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={520}>
      <ModalHead
        title={`Anuluj ${ids.length} zamówień`}
        subtitle="OPERACJA ZBIORCZA"
        onClose={onClose}
      />
      <div style={{ padding: 22 }}>
        <p style={{ margin: 0, fontSize: 13 }}>
          Każdy klient dostanie maila o anulowaniu. Zwrot środków robisz osobno · operacji nie
          cofniesz.
        </p>
        <div className="mono" style={{ marginTop: 10, fontSize: 12, color: 'var(--admin-muted)' }}>
          {ids.join(' · ')}
        </div>
        {error && <BulkError message={error} />}
        <div className="field" style={{ marginTop: 14 }}>
          <label htmlFor="bulk-cancel-reason" style={{ fontSize: 11 }}>
            Powód (wymagany)
          </label>
          <textarea
            id="bulk-cancel-reason"
            className="input-a"
            rows={3}
            style={{ width: '100%', marginTop: 4 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="field" style={{ marginTop: 8 }}>
          <label htmlFor="bulk-cancel-refund" style={{ fontSize: 11 }}>
            Notatka o zwrocie środków (opcjonalna)
          </label>
          <textarea
            id="bulk-cancel-refund"
            className="input-a"
            rows={2}
            style={{ width: '100%', marginTop: 4 }}
            value={refundNote}
            onChange={(e) => setRefundNote(e.target.value)}
          />
        </div>
      </div>
      <ModalFoot>
        <button className="btn-a" onClick={onClose} disabled={submitting}>
          Zamknij
        </button>
        <button
          className="btn-a danger"
          onClick={submit}
          disabled={submitting || reason.trim().length === 0}
        >
          Anuluj zamówienia
        </button>
      </ModalFoot>
    </ModalShell>
  );
}

function BulkError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: 12,
        padding: '8px 12px',
        border: '1px solid #C9483A',
        color: '#C9483A',
        fontSize: 12,
      }}
    >
      {message}
    </div>
  );
}

// The bulk endpoints refuse the whole selection when any one order is in the wrong state and name
// the offenders, which is the only thing that tells an operator which row to deselect.
function bulkErrorMessage(err: unknown, verb: string): string {
  const body = (err as { body?: { error?: string; ineligible?: string[] } }).body;
  const ineligible = body?.ineligible?.length ? ` (${body.ineligible.join(', ')})` : '';
  switch (body?.error) {
    case 'some_not_found':
      return 'Części zaznaczonych zamówień już nie ma · odśwież listę.';
    case 'some_not_awaiting_payment':
      return `Nie wszystkie zaznaczone czekają na płatność${ineligible} · nic nie zostało zmienione.`;
    case 'some_not_cancellable':
      return `Nie wszystkie zaznaczone można anulować${ineligible} · nic nie zostało zmienione.`;
    case 'pending_ride_alongs':
      return 'Do któregoś z zamówień dołącza inna paczka · anuluj je pojedynczo.';
    default:
      return `Nie udało się oznaczyć zaznaczonych jako ${verb}.`;
  }
}

function shippingShort(o: AdminOrderRow) {
  const addr = o.shippingAddress;
  if (o.shippingMethod === 'paczkomat') return `PCZ ${addr.paczkomat?.code ?? ''}`;
  if (o.shippingMethod === 'kurier') return `KUR ${addr.courier?.city ?? ''}`;
  if (o.shippingMethod === 'zagranica') {
    return `ZAGR ${addr.international?.countryName ?? addr.international?.country ?? ''}`;
  }
  return 'ODBIÓR OSOB.';
}

function timeShort(iso: string) {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm} · ${hh}:${mi}`;
}
